import { academyVersionRequest, createVersionedCommandSender } from "../../../shared/academyApi";

export type ProctorExamAccommodation = {
  id: string;
  exam_session_id: string;
  candidate_registration_id: string;
  candidate_user_id: string;
  candidate_name: string;
  assessment_title: string;
  accommodation_type: string;
  functional_instruction: string;
  extra_time_minutes: number | null;
  break_duration_minutes: number | null;
  max_breaks: number | null;
  status: "approved";
  effective_at: string;
  expires_at: string;
  approved_by: string;
  approved_at: string;
  application_id: string | null;
  application_state: "applied" | null;
  applied_at: string | null;
  support_coverage_ends_at: string | null;
  break_count: number;
  break_seconds_consumed: number;
  open_break_id: string | null;
  open_break_started_at: string | null;
  authority_snapshot_hash: string;
  schema_version: "d12-exam-accommodation.v1";
  version: 2;
};
export type SupervisedBreakEventReceipt = {
  id: string;
  exam_session_id: string;
  candidate_registration_id: string;
  candidate_user_id: string;
  approved_accommodation_id: string;
  accommodation_application_id: string;
  accommodation_version: 2;
  event_type: "started" | "ended";
  break_sequence: number;
  break_id: string;
  duration_limit_seconds: number;
  elapsed_seconds: number | null;
  credited_seconds: number | null;
  recorded_by: string;
  recorded_at: string;
  authority_snapshot_hash: string;
  schema_version: "d12-supervised-break-event.v1";
  remaining_breaks: number;
  roomClockChanged: false;
  attemptChanged: false;
  academicOutcome: false;
};
export type ExamAccommodationApplicationReceipt = {
  id: string;
  exam_session_id: string;
  approved_accommodation_id: string;
  support_coverage_ends_at: string;
  delivery_state: "applied";
  applied_at: string;
  schema_version: "d12-exam-accommodation-application.v1";
  attemptChanged: false;
  academicOutcome: false;
};
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const hash = /^[0-9a-f]{64}$/;
const kinds = new Set([
  "timing_extension",
  "supervised_breaks",
  "assistive_technology",
  "captions_interpretation",
  "alternative_verification",
  "approved_material",
  "delivery_adjustment",
]);
const within = (value: unknown, minimum: number, maximum: number) =>
  typeof value === "number" && Number.isInteger(value) && value >= minimum && value <= maximum;
function parse(value: unknown): ProctorExamAccommodation {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Accommodation record is invalid");
  const row = value as Record<string, unknown>;
  if (
    !uuid.test(String(row.id)) ||
    !uuid.test(String(row.exam_session_id)) ||
    !uuid.test(String(row.candidate_registration_id)) ||
    !uuid.test(String(row.candidate_user_id)) ||
    !uuid.test(String(row.approved_by)) ||
    typeof row.candidate_name !== "string" ||
    !row.candidate_name ||
    typeof row.assessment_title !== "string" ||
    !row.assessment_title ||
    !kinds.has(String(row.accommodation_type)) ||
    typeof row.functional_instruction !== "string" ||
    row.functional_instruction.length < 5 ||
    row.status !== "approved" ||
    row.schema_version !== "d12-exam-accommodation.v1" ||
    row.version !== 2 ||
    !hash.test(String(row.authority_snapshot_hash)) ||
    typeof row.effective_at !== "string" ||
    typeof row.expires_at !== "string" ||
    typeof row.approved_at !== "string"
  )
    throw new Error("Accommodation record violated its closed contract");
  const applied = row.application_state === "applied";
  if (!(
    (row.application_id === null &&
      row.application_state === null &&
      row.applied_at === null &&
      row.support_coverage_ends_at === null) ||
    (applied &&
      uuid.test(String(row.application_id)) &&
      typeof row.applied_at === "string" &&
      typeof row.support_coverage_ends_at === "string")
  ))
    throw new Error("Accommodation application projection is invalid");
  if (
    !within(row.break_count, 0, 20) ||
    !within(row.break_seconds_consumed, 0, 1_000_000) ||
    !(
      (row.open_break_id === null && row.open_break_started_at === null) ||
      (uuid.test(String(row.open_break_id)) && typeof row.open_break_started_at === "string")
    )
  )
    throw new Error("Supervised-break projection is invalid");
  for (const key of ["extra_time_minutes", "break_duration_minutes", "max_breaks"])
    if (row[key] !== null && !Number.isInteger(row[key])) throw new Error("Accommodation limits are invalid");
  if (
    (row.accommodation_type === "timing_extension" &&
      (!within(row.extra_time_minutes, 1, 240) ||
        row.break_duration_minutes !== null ||
        row.max_breaks !== null)) ||
    (row.accommodation_type === "supervised_breaks" &&
      (!within(row.break_duration_minutes, 1, 60) ||
        !within(row.max_breaks, 1, 20) ||
        row.extra_time_minutes !== null)) ||
    (!["timing_extension", "supervised_breaks"].includes(String(row.accommodation_type)) &&
      (row.extra_time_minutes !== null || row.break_duration_minutes !== null || row.max_breaks !== null))
  )
    throw new Error("Accommodation limits violated their type contract");
  return row as ProctorExamAccommodation;
}
function applicationReceipt(value: unknown): ExamAccommodationApplicationReceipt {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Accommodation application receipt is invalid");
  const row = value as Record<string, unknown>;
  if (
    !uuid.test(String(row.id)) ||
    !uuid.test(String(row.exam_session_id)) ||
    !uuid.test(String(row.approved_accommodation_id)) ||
    typeof row.support_coverage_ends_at !== "string" ||
    row.delivery_state !== "applied" ||
    typeof row.applied_at !== "string" ||
    row.schema_version !== "d12-exam-accommodation-application.v1" ||
    row.attemptChanged !== false ||
    row.academicOutcome !== false
  )
    throw new Error("Accommodation application receipt violated its closed contract");
  return row as ExamAccommodationApplicationReceipt;
}
function breakReceipt(value: unknown): SupervisedBreakEventReceipt {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Supervised-break receipt is invalid");
  const row = value as Record<string, unknown>;
  if (
    !uuid.test(String(row.id)) ||
    !uuid.test(String(row.exam_session_id)) ||
    !uuid.test(String(row.candidate_registration_id)) ||
    !uuid.test(String(row.candidate_user_id)) ||
    !uuid.test(String(row.approved_accommodation_id)) ||
    !uuid.test(String(row.accommodation_application_id)) ||
    row.accommodation_version !== 2 ||
    !(["started", "ended"] as unknown[]).includes(row.event_type) ||
    !within(row.break_sequence, 1, 20) ||
    !uuid.test(String(row.break_id)) ||
    !within(row.duration_limit_seconds, 60, 3600) ||
    !uuid.test(String(row.recorded_by)) ||
    typeof row.recorded_at !== "string" ||
    !hash.test(String(row.authority_snapshot_hash)) ||
    row.schema_version !== "d12-supervised-break-event.v1" ||
    !within(row.remaining_breaks, 0, 20) ||
    row.roomClockChanged !== false ||
    row.attemptChanged !== false ||
    row.academicOutcome !== false ||
    (row.event_type === "started" && (row.elapsed_seconds !== null || row.credited_seconds !== null)) ||
    (row.event_type === "ended" &&
      (!within(row.elapsed_seconds, 0, 1_000_000) || !within(row.credited_seconds, 0, 3600)))
  )
    throw new Error("Supervised-break receipt violated its closed contract");
  return row as SupervisedBreakEventReceipt;
}
const sendApplication = createVersionedCommandSender("d12-exam-accommodation-application");
const sendBreak = createVersionedCommandSender("d12-supervised-break-event");
export async function listApprovedExamAccommodations(examSessionId: string, signal?: AbortSignal) {
  const value = await academyVersionRequest<unknown>(
    `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/accommodations`,
    undefined,
    "GET",
    true,
    undefined,
    undefined,
    signal,
  );
  if (!Array.isArray(value)) throw new Error("Accommodation response must be a list");
  if (value.length > 100) throw new Error("Accommodation response exceeded its bounded contract");
  return value.map(parse);
}
export async function applyApprovedExamAccommodation(
  examSessionId: string,
  accommodationId: string,
  expectedAccommodationVersion: 2,
  reason: string,
) {
  return applicationReceipt(
    await sendApplication<unknown>(
      `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/accommodations/${encodeURIComponent(accommodationId)}/applications`,
      { expectedAccommodationVersion, reason },
      expectedAccommodationVersion,
    ),
  );
}
export async function recordSupervisedBreak(
  examSessionId: string,
  candidateUserId: string,
  accommodationApplicationId: string,
  expectedBreakCount: number,
  action: "start" | "end",
  breakId: string | null,
  reason: string,
) {
  return breakReceipt(
    await sendBreak<unknown>(
      `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/candidates/${encodeURIComponent(candidateUserId)}/breaks`,
      { accommodationApplicationId, expectedBreakCount, action, ...(breakId ? { breakId } : {}), reason },
      expectedBreakCount,
    ),
  );
}
